import { css } from '@emotion/react';
import { BREAKPOINTS, IconButton, SvgIcon, tokens, icons } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { Fragment } from 'react';

import { useMediaQuery } from '../../hooks';

const styles = {
  smallMenuContainer: css`
    position: fixed;
    top: 0;
    bottom: 0;
    z-index: 200;
    background-color: ${tokens.color.white};
    transition: width 0.2s ease-in-out;
    overflow: hidden;
  `,
  mobileMenuContainer: (isExpanded: boolean) => css`
    width: ${isExpanded ? '100%' : '0px'};
    right: 0;
  `,
  laptopMenuContainer: (isExpanded: boolean) => css`
    width: ${isExpanded ? '418px' : '0px'};
    right: 0;
    box-shadow: ${tokens.shadow.small};
  `,
  desktopMenuBackground: css`
    width: 64px;
  `,
  desktopMenuContainer: (isExpanded: boolean) => css`
    width: ${isExpanded ? '240px' : '64px'};
    height: 100vh;
    left: 0;
    box-shadow: ${tokens.shadow.small};
  `,
  largeDesktopMenuContainer: (isExpanded: boolean) => css`
    position: relative;
    background-color: ${tokens.color.white};
    width: ${isExpanded ? '240px' : '64px'};
    transition: width 0.4s ease-in-out;
    min-height: 100vh;
    box-shadow: ${tokens.shadow.small};
  `,
  menu: css`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
  menuItem: (isActive: boolean) => css`
    display: flex;
    margin: 0 8px;
    border-radius: ${tokens.cornerRadius.medium};
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    align-items: center;
    background-color: ${isActive ? tokens.color.blue[40] : tokens.color.white};

    &:hover {
      background-color: ${isActive ? tokens.color.gray[50] : tokens.color.gray[90]};
    }
  `,
  menuIconWrapper: css`
    width: 48px;
    height: 48px;
    min-width: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  menuIconLabel: (isActive: boolean, isExpanded: boolean) => css`
    flex: 1;
    color: ${isActive ? tokens.color.white : tokens.color.black[100]};
    opacity: ${isExpanded ? 0.8 : 0};
    transition: opacity 0.4s ease-in-out;
    text-indent: 8px;
  `,
  closeMenueContainer: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 12px 8px 40px;
  `,
  backdrop: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 100;
  `,
  hr: css`
    border-top: 1px solid ${tokens.color.gray[90]};
    margin: 0 16px;
  `,
};

type MenuItem = {
  label: string;
  iconName: keyof typeof icons;
  isActive?: boolean;
};

type MenuItems = MenuItem[][];

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export function Menu({ isOpen, setIsOpen }: Props) {
  const isMobile = useMediaQuery({ max: BREAKPOINTS.layout.sm });
  const isLaptop = useMediaQuery({ min: BREAKPOINTS.layout.sm + 1, max: BREAKPOINTS.layout.md });
  const isDesktop = useMediaQuery({ min: BREAKPOINTS.layout.md + 1, max: BREAKPOINTS.layout.lg });
  const isLargeDesktop = useMediaQuery({ min: BREAKPOINTS.layout.lg + 1 });

  const showBackdrop = !isLargeDesktop && isOpen;

  const menu = <MenuContent isOpen={isOpen} setIsOpen={setIsOpen} />;

  return (
    <>
      {showBackdrop && <div css={styles.backdrop} onClick={() => setIsOpen(false)} />}

      {isMobile && <div css={[styles.smallMenuContainer, styles.mobileMenuContainer(isOpen)]}>{menu}</div>}
      {isLaptop && <div css={[styles.smallMenuContainer, styles.laptopMenuContainer(isOpen)]}>{menu}</div>}
      {isDesktop && (
        <>
          <div css={styles.desktopMenuBackground} />
          <div css={[styles.smallMenuContainer, styles.desktopMenuContainer(isOpen)]}>{menu}</div>
        </>
      )}

      {isLargeDesktop && <div css={styles.largeDesktopMenuContainer(isOpen)}>{menu}</div>}
    </>
  );
}

const menuItems: MenuItems = [
  [
    { label: 'Flight', iconName: 'Plane' },
    { label: 'Hotel', iconName: 'Hotel' },
    { label: 'Car', iconName: 'RentalCar' },
  ],
  [
    { label: 'My trip', iconName: 'Airport' },
    { label: 'EuroBonus', iconName: 'EBCard', isActive: true },
    { label: 'SAS Bonus trips', iconName: 'BagChecked' },
  ],
  [
    { label: 'Destinations', iconName: 'Globe' },
    { label: 'LP Calendar', iconName: 'Calendar' },
  ],
  [
    { label: 'Travel info', iconName: 'Info' },
    { label: 'Help & Support', iconName: 'HelpCircle' },
  ],
];

function MenuContent({ isOpen, setIsOpen }: Props) {
  const menuItemComponents = menuItems.map(items => {
    return items.map(({ label, iconName, isActive }, index) => {
      const isLast = index === items.length - 1;
      return (
        <Fragment key={label}>
          <div css={styles.menuItem(!!isActive)}>
            <div css={styles.menuIconWrapper}>
              <SvgIcon icon={iconName} size="small" color={isActive ? 'white' : 'text'} />
            </div>
            <div css={styles.menuIconLabel(!!isActive, isOpen)}>{label}</div>
          </div>
          {isLast && <div css={styles.hr} />}
        </Fragment>
      );
    });
  });

  return (
    <>
      <div css={styles.closeMenueContainer}>
        <IconButton
          ariaLabel={t('common:menu', { defaultValue: 'Menu' })}
          variant="link"
          size="medium"
          icon={'Menu'}
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>

      <div css={styles.menu}>{menuItemComponents.flat()}</div>
    </>
  );
}
